import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsStars, BsImage, BsMagic } from 'react-icons/bs';
import ChatMessage from './ChatMessage';
import LoadingSkeleton from './LoadingSkeleton';

const ImageChatMessages = forwardRef(({ 
    messages, 
    isGenerating, 
    aspectRatio,
    onDeleteMessage,
    isSelectable = false,
    handleOnSelect,
    handleOnUnselect,
    selectedImages = []
}, ref) => {
    const isEmpty = messages.length === 0 && !isGenerating;

    return (
        <div className={`overflow-y-auto w-full relative flex-1 bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50 dark:from-gray-900/50 dark:via-gray-900 dark:to-gray-900/50 !h-full`} ref={ref}>
            <div className={`p-3 lg:p-6 space-y-4 lg:space-y-6 max-w-[900px] w-full mx-auto min-h-full ${isEmpty && `flex flex-col items-center justify-center`}`}>
                <AnimatePresence mode="wait">
                    {isEmpty && (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.3 }}
                            className="flex flex-col items-center justify-center h-full text-center py-8 lg:py-16 px-4"
                        >
                            {/* Animated Icon */}
                            <motion.div
                                animate={{ 
                                    y: [0, -10, 0],
                                }}
                                transition={{ 
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                className="relative mb-6 lg:mb-8"
                            >
                                <div className="relative p-6 lg:p-8 bg-gradient-to-br from-pink-500/10 via-orange-500/10 to-rose-500/10 dark:from-pink-500/20 dark:via-orange-500/20 dark:to-rose-500/20 rounded-3xl backdrop-blur-sm">
                                    <motion.div
                                        animate={{ 
                                            rotate: [0, 5, -5, 0],
                                            scale: [1, 1.05, 1]
                                        }}
                                        transition={{ 
                                            duration: 4,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <BsImage className="w-12 h-12 lg:w-16 lg:h-16 text-pink-500" />
                                    </motion.div>
                                    
                                    {/* Floating sparkles */}
                                    <motion.div
                                        animate={{ 
                                            x: [0, 10, 0],
                                            y: [0, -10, 0],
                                            opacity: [0.5, 1, 0.5]
                                        }}
                                        transition={{ 
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                        className="absolute -top-2 -right-2"
                                    >
                                        <BsStars className="w-5 h-5 lg:w-6 lg:h-6 text-orange-500" />
                                    </motion.div>
                                    
                                    <motion.div
                                        animate={{ 
                                            x: [0, -10, 0],
                                            y: [0, 10, 0],
                                            opacity: [0.5, 1, 0.5]
                                        }}
                                        transition={{ 
                                            duration: 2.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: 0.5
                                        }}
                                        className="absolute -bottom-2 -left-2"
                                    >
                                        <BsMagic className="w-5 h-5 lg:w-6 lg:h-6 text-rose-500" />
                                    </motion.div>
                                </div>
                                
                                {/* Pulse ring */}
                                <motion.div
                                    animate={{ 
                                        scale: [1, 1.3, 1],
                                        opacity: [0.3, 0, 0.3]
                                    }}
                                    transition={{ 
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeOut"
                                    }}
                                    className="absolute inset-0 border-2 border-pink-500 rounded-3xl"
                                />
                            </motion.div>
                            
                            <motion.h3
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="text-lg lg:text-2xl font-black bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent mb-3"
                            >
                                Transform Your Images with AI
                            </motion.h3>
                            
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-sm lg:text-base text-gray-600 dark:text-gray-400 max-w-md leading-relaxed"
                            >
                                Select images below and describe how you want to modify them. 
                                The AI will create stunning variations based on your instructions.
                            </motion.p>
                            
                            {/* Feature pills */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="flex flex-wrap gap-2 justify-center mt-6"
                            >
                                {['Edit Style', 'Change Colors', 'Add Objects'].map((feature, index) => (
                                    <motion.div
                                        key={feature}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.4 + index * 0.1 }}
                                        className="px-3 py-1.5 bg-gradient-to-r from-pink-500/10 to-orange-500/10 dark:from-pink-500/20 dark:to-orange-500/20 rounded-full border border-pink-200/50 dark:border-pink-500/30"
                                    >
                                        <span className="text-xs font-medium text-pink-700 dark:text-pink-300">
                                            {feature}
                                        </span>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
                
                <AnimatePresence mode="popLayout">
                    {messages.map((message, index) => (
                        <motion.div
                            key={message.id}
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.95 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                            <ChatMessage 
                                message={message} 
                                aspectRatio={aspectRatio}
                                onDeleteMessage={onDeleteMessage}
                                isSelectable={isSelectable}
                                handleOnSelect={handleOnSelect}
                                handleOnUnselect={handleOnUnselect}
                                selectedImages={selectedImages}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
                
                <AnimatePresence>
                    {isGenerating && (
                        <motion.div
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.95 }}
                            transition={{ duration: 0.3 }}
                        >
                            <LoadingSkeleton aspectRatio={aspectRatio} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
});

ImageChatMessages.displayName = 'ImageChatMessages';

export default ImageChatMessages; 